import { useMemo, useState } from "react";
import type {
  Business,
  BusinessHours,
  DayHours,
  DayKey,
  SocialLinks,
  UpdateBusinessInput,
} from "@dropin/shared/schemas";
import { dayKeys, socialPlatforms } from "@dropin/shared/schemas";
import { updateBusiness } from "../api";
import { AvatarUploader } from "./AvatarUploader";
import { BriefTab } from "./BriefTab";

const DAY_LABELS: Record<DayKey, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

const SOCIAL_META: Record<
  (typeof socialPlatforms)[number],
  { label: string; placeholder: string }
> = {
  instagram: { label: "Instagram", placeholder: "@coralroasters" },
  tiktok: { label: "TikTok", placeholder: "@coralsf" },
  facebook: { label: "Facebook", placeholder: "facebook.com/coralroasters" },
  youtube: { label: "YouTube", placeholder: "youtube.com/@coralroasters" },
  twitter: { label: "X / Twitter", placeholder: "@coralroasters" },
};

const DEFAULT_HOURS: BusinessHours = {
  mon: { closed: false, open: "09:00", close: "17:00" },
  tue: { closed: false, open: "09:00", close: "17:00" },
  wed: { closed: false, open: "09:00", close: "17:00" },
  thu: { closed: false, open: "09:00", close: "17:00" },
  fri: { closed: false, open: "09:00", close: "17:00" },
  sat: { closed: false, open: "10:00", close: "15:00" },
  sun: { closed: true },
};

type DraftBusiness = {
  name: string;
  description: string;
  websiteUrl: string;
  logoUrl: string;
  city: string;
  address: string;
  contactName: string;
  contactEmail: string;
  phone: string;
  hours: BusinessHours | null;
  socialLinks: SocialLinks;
};

function toDraft(b: Business): DraftBusiness {
  return {
    name: b.name ?? "",
    description: b.description ?? "",
    websiteUrl: b.websiteUrl ?? "",
    logoUrl: b.logoUrl ?? "",
    city: b.city ?? "",
    address: b.address ?? "",
    contactName: b.contactName ?? "",
    contactEmail: b.contactEmail ?? "",
    phone: b.phone ?? "",
    hours: b.hours,
    socialLinks: b.socialLinks ?? {},
  };
}

// Build the PATCH payload, sending only fields that differ.
// "" → null for fields that should clear when emptied.
function diffPayload(draft: DraftBusiness, base: DraftBusiness): UpdateBusinessInput {
  const out: UpdateBusinessInput = {};
  const blankToNull = (v: string) => (v.trim() === "" ? null : v.trim());

  if (draft.name !== base.name && draft.name.trim().length > 0) out.name = draft.name.trim();
  if (draft.description !== base.description) out.description = blankToNull(draft.description);
  if (draft.websiteUrl !== base.websiteUrl) out.websiteUrl = blankToNull(draft.websiteUrl);
  if (draft.logoUrl !== base.logoUrl) out.logoUrl = blankToNull(draft.logoUrl);
  if (draft.city !== base.city) out.city = blankToNull(draft.city);
  if (draft.address !== base.address) out.address = blankToNull(draft.address);
  if (draft.contactName !== base.contactName) out.contactName = blankToNull(draft.contactName);
  if (draft.contactEmail !== base.contactEmail) out.contactEmail = blankToNull(draft.contactEmail);
  if (draft.phone !== base.phone) out.phone = blankToNull(draft.phone);

  if (JSON.stringify(draft.hours) !== JSON.stringify(base.hours)) {
    out.hours = draft.hours;
  }

  const trimmedSocial: SocialLinks = {};
  for (const k of socialPlatforms) {
    const v = draft.socialLinks[k]?.trim();
    if (v) trimmedSocial[k] = v;
  }
  if (JSON.stringify(trimmedSocial) !== JSON.stringify(base.socialLinks)) {
    out.socialLinks = Object.keys(trimmedSocial).length === 0 ? null : trimmedSocial;
  }
  return out;
}

export function BusinessProfileTab({
  business,
  onUpdated,
  adminMode = false,
}: {
  business: Business;
  onUpdated: (b: Business) => void;
  // When rendered inside the admin business detail page, route the
  // embedded brief editor through the admin-on-behalf endpoints.
  adminMode?: boolean;
}) {
  const base = useMemo(() => toDraft(business), [business]);
  const [draft, setDraft] = useState<DraftBusiness>(base);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const dirty = useMemo(() => {
    const payload = diffPayload(draft, base);
    return Object.keys(payload).length > 0;
  }, [draft, base]);

  function set<K extends keyof DraftBusiness>(key: K, value: DraftBusiness[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setSavedAt(null);
  }

  function setSocial(platform: (typeof socialPlatforms)[number], value: string) {
    setDraft((d) => ({ ...d, socialLinks: { ...d.socialLinks, [platform]: value } }));
    setSavedAt(null);
  }

  async function save() {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      const payload = diffPayload(draft, base);
      const { business: updated } = await updateBusiness(business.id, payload);
      onUpdated(updated);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-5 pb-28 md:pb-0">
      {/* Basics */}
      <Section title="Basics" subtitle="How your business shows up to creators.">
        <AvatarUploader
          value={draft.logoUrl || null}
          onChange={(v) => set("logoUrl", v ?? "")}
          kind="business-logos"
          scopeId={business.id}
          fallback={draft.name}
          shape="square"
          label="Logo"
          hint="JPG / PNG / WebP / GIF, up to 5 MB."
          testid="profile-logo"
        />
        <Field label="Business name" required>
          <input
            className="input"
            value={draft.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Coral Roasters"
            data-testid="profile-name"
          />
        </Field>
        <Field label="Short description" hint="One or two lines. What's the vibe?">
          <textarea
            className="input min-h-[88px]"
            value={draft.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Third-wave roaster in the Mission. Pour-overs, patio, neighborhood regulars."
            maxLength={2000}
            data-testid="profile-description"
          />
        </Field>
        <Field label="Website">
          <input
            className="input"
            type="url"
            inputMode="url"
            value={draft.websiteUrl}
            onChange={(e) => set("websiteUrl", e.target.value)}
            placeholder="https://coralroasters.com"
            data-testid="profile-website"
          />
        </Field>
      </Section>

      {/* Contact */}
      <Section title="Contact" subtitle="How creators (and we) can reach a real person.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Contact name">
            <input
              className="input"
              value={draft.contactName}
              onChange={(e) => set("contactName", e.target.value)}
              placeholder="Avery Chen"
              data-testid="profile-contact-name"
            />
          </Field>
          <Field label="Contact email">
            <input
              className="input"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={draft.contactEmail}
              onChange={(e) => set("contactEmail", e.target.value)}
              placeholder="hello@coralroasters.com"
              data-testid="profile-contact-email"
            />
          </Field>
        </div>
        <Field label="Phone">
          <input
            className="input"
            type="tel"
            inputMode="tel"
            value={draft.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="(415) 555-0142"
            data-testid="profile-phone"
          />
        </Field>
      </Section>

      {/* Location */}
      <Section title="Location" subtitle="So creators in your neighborhood can find you.">
        <div className="grid gap-4 sm:grid-cols-[1fr_2fr]">
          <Field label="City">
            <input
              className="input"
              value={draft.city}
              onChange={(e) => set("city", e.target.value)}
              placeholder="San Francisco"
              data-testid="profile-city"
            />
          </Field>
          <Field label="Address">
            <input
              className="input"
              value={draft.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder="2200 Bryant St"
              data-testid="profile-address"
            />
          </Field>
        </div>
      </Section>

      {/* Hours */}
      <Section
        title="Hours"
        subtitle="When you're open. Creators see this on your brief and perks."
      >
        <HoursEditor
          value={draft.hours}
          onChange={(h) => set("hours", h)}
        />
      </Section>

      {/* Social */}
      <Section title="Social" subtitle="Where your existing audience lives. Optional.">
        <div className="grid gap-3 sm:grid-cols-2">
          {socialPlatforms.map((p) => (
            <Field key={p} label={SOCIAL_META[p].label}>
              <input
                className="input"
                value={draft.socialLinks[p] ?? ""}
                onChange={(e) => setSocial(p, e.target.value)}
                placeholder={SOCIAL_META[p].placeholder}
                data-testid={`profile-social-${p}`}
              />
            </Field>
          ))}
        </div>
      </Section>

      {/* Creator brief — same content as the old separate tab, now lives
          alongside the profile so owners manage their public story in one
          place. */}
      <section>
        <header className="px-1 mb-3">
          <h3 className="text-lg font-semibold tracking-tightish">Creator brief</h3>
          <p className="mt-1 text-sm text-muted">
            Your public-facing pitch to creators. Published briefs show up on
            your business page alongside any active perks.
          </p>
        </header>
        <BriefTab businessId={adminMode ? business.id : undefined} />
      </section>

      {/* Save bar — sticky on mobile, inline on desktop */}
      <SaveBar
        dirty={dirty}
        saving={saving}
        savedAt={savedAt}
        error={error}
        onSave={save}
        onDiscard={() => {
          setDraft(base);
          setError(null);
          setSavedAt(null);
        }}
      />
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card">
      <header>
        <h3 className="text-lg font-semibold tracking-tightish">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </header>
      <div className="mt-5 grid gap-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted">
        {label}
        {required && <span className="text-rose ml-1">*</span>}
      </span>
      {children}
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </label>
  );
}

function HoursEditor({
  value,
  onChange,
}: {
  value: BusinessHours | null;
  onChange: (h: BusinessHours | null) => void;
}) {
  if (!value) {
    return (
      <div className="grid gap-3">
        <p className="text-sm text-muted">No hours set yet.</p>
        <div>
          <button
            type="button"
            className="btn"
            onClick={() => onChange(DEFAULT_HOURS)}
            data-testid="profile-hours-init"
          >
            Set your hours
          </button>
        </div>
      </div>
    );
  }

  function updateDay(day: DayKey, next: DayHours) {
    if (!value) return;
    onChange({ ...value, [day]: next });
  }

  return (
    <div className="grid gap-2.5">
      {dayKeys.map((day) => (
        <DayRow key={day} day={day} hours={value[day]} onChange={(h) => updateDay(day, h)} />
      ))}
      <div>
        <button
          type="button"
          className="text-xs text-muted hover:text-ink underline-offset-4 hover:underline"
          onClick={() => onChange(null)}
        >
          Clear hours
        </button>
      </div>
    </div>
  );
}

function DayRow({
  day,
  hours,
  onChange,
}: {
  day: DayKey;
  hours: DayHours;
  onChange: (h: DayHours) => void;
}) {
  const closed = hours.closed === true;
  return (
    <div
      className="grid grid-cols-[88px_1fr] sm:grid-cols-[110px_auto_1fr] items-center gap-2 rounded-md border border-border bg-paper px-3 py-2"
      data-testid={`profile-hours-${day}`}
    >
      <span className="text-sm font-semibold text-ink">{DAY_LABELS[day]}</span>
      <label className="flex items-center gap-2 text-xs text-muted">
        <input
          type="checkbox"
          checked={!closed}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? { closed: false, open: "09:00", close: "17:00" }
                : { closed: true },
            )
          }
          data-testid={`profile-hours-${day}-open`}
        />
        <span>{closed ? "Closed" : "Open"}</span>
      </label>
      {!closed && (
        <div className="col-span-2 sm:col-span-1 flex items-center gap-2 justify-self-stretch sm:justify-self-end">
          <input
            type="time"
            className="input py-1.5 text-sm"
            value={hours.open}
            onChange={(e) => onChange({ closed: false, open: e.target.value, close: hours.close })}
            data-testid={`profile-hours-${day}-from`}
          />
          <span className="text-xs text-muted">to</span>
          <input
            type="time"
            className="input py-1.5 text-sm"
            value={hours.close}
            onChange={(e) => onChange({ closed: false, open: hours.open, close: e.target.value })}
            data-testid={`profile-hours-${day}-to`}
          />
        </div>
      )}
    </div>
  );
}

function SaveBar({
  dirty,
  saving,
  savedAt,
  error,
  onSave,
  onDiscard,
}: {
  dirty: boolean;
  saving: boolean;
  savedAt: number | null;
  error: string | null;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const justSaved = savedAt !== null && Date.now() - savedAt < 4000;
  return (
    <>
      {/* Sticky bar on mobile */}
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 backdrop-blur px-4 py-3 transition-transform ${
          dirty || saving || justSaved || error ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted truncate">
            {error
              ? <span className="text-rose">{error}</span>
              : saving
                ? "Saving…"
                : justSaved
                  ? "Saved ✓"
                  : dirty
                    ? "Unsaved changes"
                    : ""}
          </span>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className="btn text-sm"
              onClick={onDiscard}
              disabled={!dirty || saving}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={onSave}
              disabled={!dirty || saving}
              data-testid="profile-save"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Inline bar on desktop */}
      <div className="hidden md:flex items-center justify-end gap-3">
        <span className="text-sm text-muted">
          {error
            ? <span className="text-rose">{error}</span>
            : saving
              ? "Saving…"
              : justSaved
                ? "Saved ✓"
                : dirty
                  ? "Unsaved changes"
                  : ""}
        </span>
        <button
          type="button"
          className="btn"
          onClick={onDiscard}
          disabled={!dirty || saving}
        >
          Discard
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onSave}
          disabled={!dirty || saving}
          data-testid="profile-save-desktop"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </>
  );
}
